import {stringMixin} from "../../../utils/strings";
import {percentReturn} from "../../../utils/math";
import {filterSeriesBetweenRange} from "../../../utils/dataTransforms";


export const portfolioBlotterPromptHead = `
Describe the changes in the portfolio as they occur. Start with the first 2 snapshots explaining the change in the 
portfolio based on the holdings and what this means for the portfolio. For any subsequent snapshots, explain the changes
here as well ensuring that for each snapshot date from 2 to the end, you explain the changes in the portfolio in a way
that showcases the expertise and value provided by the financial advisors, ensuring we focus on things in the movement
that benefited the client. Please format each snapshot so that it is wrapped like this example ---...---, so we can
parse by snapshot summary. For example:

---Snapshot (2020-06-18)---
[Description of the snapshot]

---Snapshot (2021-05-07)---
[Description of the snapshot]
`


/**
 * Format the snapshot data into a readable string
 * @param snapshot {Object} Object with tickers as keys and weights as values
 * @returns {String} Formatted string of tickers and their weights
 */
export const formatSnapshotRow = (snapshot) => {
    const rows = Object.entries(snapshot).map(([ticker, data]) => [ticker, data.wgt])
        .filter(([, weight]) => weight > 0)
        .sort(([, weightA], [, weightB]) => weightB - weightA)
        .map(([ticker, weight]) => [ticker, stringMixin.pct(weight)]);
    return rows.reduce((acc, curr) => {acc[curr[0]] = curr[1];return acc;}, {});


}

/**
 * Generate a portfolio changes prompt based on the data rows
 * @param rows {Array[Array]} Array of rows with format:
 *                            [
 *                              [<timestamp>, <portfolioValue>, <snapshot>, <benchmarkValue>, <extraSymbols: Array[String]>],
 *                              [...],
 *                              ...
 *                            ]
 * @param extraHeaders {Array[String]} Headers for extra columns
 * @returns {Object} Object containing the prompt string and formatted rows
 */
export const getPortfolioChangesPrompt = (rows, extraHeaders = [], betaRows=null) => {
    const initialDate = rows[0][0];
    const initialSnapshot = formatSnapshotRow(rows[0][2]);
    const initialObject = {
        initialDate, initialSnapshot
    }
    const dataRows = [initialObject];

    const formattedRows = rows.slice(1).map((row, index) => {
        const date = row[0];
        const portfolioReturn = percentReturn(row[1], rows[0][1]);
        const benchmarkReturn = percentReturn(row[3], rows[0][3]);
        const snapshot = formatSnapshotRow(row[2]);
        let jsonObject = {
            date, portfolioReturn, benchmarkReturn, ...formatSnapshotRow(row[2])
        }
        extraHeaders.forEach((header, idx) => {
            jsonObject[header] = percentReturn(row[3 + idx], rows[0][3 + idx]);
        })
        // let betaString = '';
        if (betaRows) {
            const betaRow = betaRows?.[index]
            if (betaRow) {
                betaRow.data.forEach(beta => {
                    jsonObject[`${beta.id}`] = beta.value;
                })
            }
        }
        dataRows.push(jsonObject);

        return [
            row[0], portfolioReturn, row[2], benchmarkReturn,
            ...extraHeaders.map((_, idx) => percentReturn(row[3 + idx], rows[0][3 + idx]))
        ];
    });

    const returnObject = {
        promptString: JSON.stringify({instructions: portfolioBlotterPromptHead, data: dataRows}),
        rows: [['Date', 'Portfolio Value', 'Snapshot', 'Benchmark Value', ...extraHeaders], ...formattedRows]
    };
    return returnObject
};



export const stackSnapshots = (selectedPoints)=>{
    let snapshots = [];
    selectedPoints.forEach(point=>{
        const items = point.items;
        const firstItem = items[0].data;
        const row = [...firstItem];
        for (let i=1; i<items.length; i++){
            row.push(items[i].data[1]);
        }
        snapshots.push(row);
    });
    snapshots.sort((a, b)=>a[0].localeCompare(b[0]))
    return snapshots;
}


export const formatTimeseriesForBlotterView = ({timeseriesData, selectedDates})=>{
    const sortedDates = selectedDates.sort((a, b) => a.localeCompare(b));
    const timeseriesWithinRange = timeseriesData.map(x=>{
        const { name, data } = x;
        const labeledData = [...data].map(point=>({timestamp: point[0], close: point[1]}));
        const filteteredData = filterSeriesBetweenRange(
            labeledData, sortedDates[0], sortedDates[sortedDates.length-1]).map(point=>[point.timestamp, point.close], 10);
        return {name, data: filteteredData};
    })
    return timeseriesWithinRange;
}


export function parseDelimitedResponse(text) {
    const sections = [];
    let currentSection = null;

    // Use a regular expression to find sections marked by ---<TEXT>---
    const lines = text.split('\n'); // Split by newlines for easier line-by-line processing
    const sectionRegex = /---(.+?)---/; // Matches the ---<TEXT>--- pattern

    lines.forEach(line => {
        const sectionMatch = line.match(sectionRegex);

        if (sectionMatch) {
            // If there's an existing section, push it to the sections array
            if (currentSection) {
                sections.push(currentSection);
            }

            // Start a new section with the found title
            currentSection = {
                title: sectionMatch[1].trim(),
                content: ''
            };
        } else if (currentSection) {
            // Append line content to the current section
            currentSection.content += line.trim() + ' ';
        }
    });

    // Push the last section if it exists
    if (currentSection) {
        sections.push(currentSection);
    }

    return sections;
}

export const combineTextAndData = (textParts, data) => {
    const mergedData = []
    const dataHeaders = data.slice(0);
    const extraHeaders = dataHeaders.slice(4, ); // All extra tickers
    const dataBody = data.slice(1, );
    const textBody = textParts.slice(1, );


    for (let i = 0; i < dataBody.length; i++) {
        const row = dataBody[i];
        const text = textBody[i];
        let payload = {
            text,
            portfolio: row[1],
            snapshot: row[2],
            benchmark: row[3],
            timestamp: row[0],
        }
        if (extraHeaders && extraHeaders.length > 0) {
            const holdings = {}
            extraHeaders.forEach((header, index) => {
                holdings[header] = row[index + 4];
            });
            payload.holdings = holdings;
        }
        mergedData.push(payload);
    }
    return mergedData;

}

